import { Fragment, useContext, useState, useEffect, useRef } from "react";
import { Context } from "Context";
import Separator from "components/shared/Separator";
import useForm from "hooks/useForm";
import Button from "components/shared/Button";
import InputText from "components/shared/InputText";
import InputTextArea from "components/shared/InputTextArea";
import InputSelect from "components/shared/InputSelect";
import FixedToolbar from "components/shared/FixedToolbar";
import InlineErrors from "components/shared/InlineErrors";
import FileList from "components/shared/FileList";
import Image from "components/shared/Image";
import useCheckMediaQuery from "hooks/useCheckMediaQuery";
import {
	objectIsEmpty,
	isCurrentPathPresent,
	parseBoolean,
	groupForm,
	isCustomForm,
	shouldShowInineErrors,
} from "utils/common";
import {
	getEmailServiceBody,
	initializeInputsWithUrlParameters,
} from "utils/email";
import useFetch from "hooks/useFetch";
import AdditionalFields from "components/shared/AdditionalFields";
import {
	getSelectedOptionAdditionalFieldsLinkedTo,
	isFormEmpty,
} from "utils/form";
import { trackLink } from "utils/analytics";
import LottieAnimation from "components/shared/LottieAnimation";
import Icon from "components/shared/Icon";

const cacheMinutes = 0;
const owlImage = "owl.png";
const mainClass = "help-email";
const csrfTokenName = "csrf-token";

const handleTriggerAnalytics = (event, linkId) => {
	if (linkId) {
		linkId = `${linkId}_${event.type === "click" ? "CLICK" : "HOVER"}`;
		trackLink({ linkId });
	}
};

const scrollToRef = (ref) => {
	ref?.current?.scrollIntoView({
		behavior: "smooth",
		block: "start",
		inline: "nearest",
	});
};

const getFieldsToHide = (config) => {
	if (config.hideInMainForm) {
		for (const fieldsToHidde of config.hideInMainForm) {
			if (isCurrentPathPresent(fieldsToHidde.path)) {
				return fieldsToHidde.fields;
			}
		}
	}
};

const Email = ({
	theme = [],
	emailData,
	"csrf-token": csrfToken,
	pageHeader,
}) => {
	const { inputs = {}, buttons = {}, formGroups = [] } = emailData?.emailForm;
	const { successful: successfulModal = {}, discard: discardModal = {} } =
		emailData?.modal || {};
	const fields = [];
	emailData?.defaults?.map((element) => fields.push(inputs[element]));
	const defaultFields = { fields: fields };

	const {
		title,
		lastName,
		firstName,
		name,
		telephone,
		emailAddress,
		country,
		subject,
		emailMessage,
		reservationNumber,
		category,
		subcategory,
		fileList,
		multipleAttractions,
	} = inputs;

	const { primary, secondary, linkButton } = buttons;
	const {
		config,
		profileData: { user },
		setModal,
	} = useContext(Context);
	const {
		formInputs,
		additionalFields,
		linkedFields,
		inlineErrors,
		handleChange,
		addInputValue,
		removeInputValue,
		validateInput,
		validateInputFile,
		validateInputDate,
		validateForm,
		focusFirstInvalidField,
		handleMultipleFiles,
		removeFile,
	} = useForm(initializeInputsWithUrlParameters({ ...inputs }), {}, formGroups);

	const configFromContent =
		!!theme?.hyperion?.allowedPagesToApplyThemes?.length;
	const useHyperionTheme = isCurrentPathPresent(
		configFromContent
			? theme?.hyperion?.allowedPagesToApplyThemes
			: config.themes?.hyperion?.allowedPagesToApplyThemes
	);
	const [showGenericErrorMessage, setShowGenericErrorMessage] = useState(false);
	const [fileRemoved, setFileRemoved] = useState(false);
	const [genericErrorMessage, setGenericErrorMessage] = useState(
		emailData.genericErrorMessage
	);
	const [emailServiceParams, setEmailServiceParams] = useState({});
	const [isSendButtonClicked, setIsSendButtonClicked] = useState(false);
	const [conditionalsFields, setConditionalsFields] = useState([]);
	const [
		conditionalsFieldsMultipleAttraction,
		setConditionalsFieldsMultipleAttraction,
	] = useState([]);
	const [formNotes, setFormNotes] = useState(null);
	const [multipleAttractionsForms, setMultipleAttractionsForms] = useState(1);
	const multipleAttractionsfields = [];
	const multipleAttractionsfieldsGroup = groupForm(
		emailData?.multipleAttractionFields,
		emailData?.multipleAttractionGroupFields
	);
	const isDCL = config.brand === "dcl";

	multipleAttractionsfieldsGroup?.forEach((formGroupIds, index) => {
		const formGroup = [];

		formGroupIds.map((element) => {
			if (!inputs[element]?.parent) {
				formGroup.push(inputs[element]);
			}

			return element;
		});

		multipleAttractionsfields.push({
			index: index + 2,
			title: `${additionalFields.subcategory?.title} (${index + 2})`,
			fields: formGroup,
		});
	});

	const { response, error, loading } = useFetch(
		objectIsEmpty(emailServiceParams)
			? ""
			: `${config.defaultBaseApiUrl}${config.email.path}${
					config.store ? `?store=${config.store}` : ""
			  }`,
		{
			options: emailServiceParams,
			trigger: emailServiceParams,
			cacheValidMinutes: cacheMinutes,
		}
	);
	const isMobileView = useCheckMediaQuery(config.mobileMediaQuery);
	const imagesLocation = `${config.helpAssetsUrl}/images`;
	const hasInputs = !objectIsEmpty(inputs) || formGroups.length > 0;
	const categoryRef = useRef();
	const subjectRef = useRef();
	const secondaryButtonRef = useRef();
	const genericErrorMessageRef = useRef();
	const configFilesToHide = theme.hideInMainForm ? theme : config;
	const fieldsToHide = getFieldsToHide(configFilesToHide);
	const formIsEmpty = isFormEmpty(formInputs);
	const sendButtonRef = useRef();

	const checkSubjectNote = (e) => {
		if (e.target?.value) {
			const selectedSubject = getSelectedOptionAdditionalFieldsLinkedTo(
				subject,
				e.target.value
			);
			if (selectedSubject?.note) {
				setFormNotes(selectedSubject.note);
			}
		}
	};

	const getSelectedOptionBasedOnUserData = (fieldValue, userFieldValue) => {
		if (
			fieldValue?.name &&
			userFieldValue &&
			fieldValue.options?.find((option) => option.value === userFieldValue)
		) {
			addInputValue(fieldValue.name, userFieldValue);
		}
	};
	const isMultipleFile =
		document.querySelectorAll("input[type=file]").length > 1;
	const getValueBasedOnUserData = (fieldValue, userFieldValue) => {
		fieldValue?.name &&
			userFieldValue &&
			addInputValue(fieldValue.name, `${userFieldValue}`);
	};
	useEffect(() => {
		addInputValue(multipleAttractions?.name, multipleAttractionsForms);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (additionalFields.subcategory) {
			const additonalsFieldsKeys = Object.keys(additionalFields);
			const multipleAttractionsfields = [];

			setConditionalsFields(
				additionalFields.subcategory
					? additionalFields[
							additionalFields.subcategory?.fields.find((sub) => {
								return additonalsFieldsKeys.includes(sub.id);
							})?.name
					  ]?.fields
					: []
			);

			const currentAdditionalFields =
				emailData?.multipleAttractionFields?.filter((key) => {
					return additonalsFieldsKeys.includes(key);
				});

			currentAdditionalFields.forEach((currentAdditionalField) => {
				multipleAttractionsfields.push(
					...additionalFields[currentAdditionalField]?.fields
				);
			});

			setConditionalsFieldsMultipleAttraction(
				additionalFields ? multipleAttractionsfields : []
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [additionalFields, emailData]);

	useEffect(() => {
		addInputValue(multipleAttractions?.name, multipleAttractionsForms);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [multipleAttractionsForms]);

	useEffect(() => {
		if (response?.message === "Email sent") {
			// email sent successfully, we can show the sucessful confirmation modal and redirect
			if (useHyperionTheme) {
				primary?.href && window.location.assign(primary.href);
			} else {
				setModal({
					...successfulModal,
					dismissible: false,
					disableEscapeDismissal: true,
					showSeparator: true,
				});
			}
		} else if (error) {
			// show error if needed
			setShowGenericErrorMessage(true);
			emailData.sendingEmailErrorMessage &&
				setGenericErrorMessage(emailData.sendingEmailErrorMessage);
			scrollToRef(genericErrorMessageRef);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [response, error]);

	useEffect(() => {
		if (isSendButtonClicked && objectIsEmpty(inlineErrors)) {
			const headers = {};
			const formData = new FormData();
			const emailTransformedData = getEmailServiceBody(
				inputs,
				formInputs,
				additionalFields,
				emailData,
				user?.swid
			);
			const selectedCategory = getSelectedOptionAdditionalFieldsLinkedTo(
				category,
				formInputs?.category
			);
			category?.analyticsLinkId &&
				handleTriggerAnalytics(
					{ type: "click" },
					`${category.analyticsLinkId}${selectedCategory?.text
						.split(" ")
						.join("_")
						.toUpperCase()}`
				);
			primary?.analyticsLinkId &&
				handleTriggerAnalytics({ type: "click" }, primary.analyticsLinkId);

			if (csrfToken) headers[csrfTokenName] = csrfToken;
			formData.append(
				"json",
				new Blob([JSON.stringify(emailTransformedData)], {
					type: "application/json",
				})
			);
			if (formInputs.files?.length) {
				formInputs.files.forEach((file) => {
					formData.append("files", file.file);
				});
			}
			setEmailServiceParams({
				method: "POST",
				headers: headers,
				body: formData,
			});
			setIsSendButtonClicked(false);
		} else if (!objectIsEmpty(inlineErrors)) {
			if (isSendButtonClicked) {
				focusFirstInvalidField(Object.keys(inlineErrors));
				setIsSendButtonClicked(false);
			}
		}
		setShowGenericErrorMessage(!objectIsEmpty(inlineErrors));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inlineErrors, isSendButtonClicked]);

	useEffect(() => {
		if (useHyperionTheme && user) {
			name?.name &&
				user?.firstName &&
				addInputValue(name.name, `${user.firstName} ${user?.lastName || ""}`);
			getValueBasedOnUserData(firstName, user.firstName);
			getValueBasedOnUserData(lastName, user.lastName);
			getValueBasedOnUserData(telephone, user.telephone);
			getValueBasedOnUserData(emailAddress, user.email);
			getSelectedOptionBasedOnUserData(title, user.title);
			getSelectedOptionBasedOnUserData(country, user.country);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [useHyperionTheme, name, emailAddress, user]);

	const sendEmail = () => {
		setIsSendButtonClicked(true);
		validateForm();
	};

	const acceptPersonalData = () => {
		return (
			formInputs.consentProcedures &&
			formInputs.personalDataForMarketing &&
			formInputs.personalInformationCollectionStatement
		);
	};

	const renderFirstnameField = (showInlineErrors = false) => {
		return firstName ? (
			<Fragment>
				<InputText
					{...firstName}
					isHyperionTheme={useHyperionTheme}
					inlineErrors={inlineErrors}
					value={formInputs.firstName}
					handleChange={handleChange}
					validateInput={validateInput}
					showInlineErrors={showInlineErrors}
					isMobileView={isMobileView}
				/>
				{inlineErrors && isMobileView && !isCustomForm() && !isDCL && (
					<InlineErrors
						validate={[firstName?.id]}
						errorMessages={inlineErrors}
						modifier="group"
					/>
				)}
			</Fragment>
		) : null;
	};

	const renderLastnameField = (showInlineErrors = false) => {
		return lastName ? (
			<InputText
				{...lastName}
				isHyperionTheme={useHyperionTheme}
				inlineErrors={inlineErrors}
				value={formInputs.lastName}
				handleChange={handleChange}
				validateInput={validateInput}
				showInlineErrors={showInlineErrors}
				isMobileView={isMobileView}
			/>
		) : null;
	};

	const renderFirstNameLastNameFields = () => {
		return config.isLastnameFirst ? (
			<Fragment>
				{renderLastnameField()}
				{renderFirstnameField()}
			</Fragment>
		) : (
			<Fragment>
				{renderFirstnameField(shouldShowInineErrors)}
				{renderLastnameField(shouldShowInineErrors)}
			</Fragment>
		);
	};

	const renderButtons = (
		<Fragment>
			<Button
				ref={secondaryButtonRef}
				className={`${mainClass}__button ${mainClass}__button--secondary`}
				secondary
				link={!objectIsEmpty(discardModal) && !formIsEmpty ? false : true}
				linkContent={{
					href: secondary.href,
					title: secondary.label,
					accessibleText: secondary.label,
					analyticsLinkId: formIsEmpty
						? secondary?.analyticsLinkIdWithoutContent
						: secondary?.analyticsLinkIdWithContent,
					trackHover: true,
				}}
				title={secondary.label}
				analyticsLinkId={secondary.analyticsLinkIdWithContent}
				trackHover
				onClick={() => {
					if (!objectIsEmpty(discardModal) && !formIsEmpty) {
						setModal({
							...discardModal,
							dismissible: true,
							showSeparator: !useHyperionTheme,
							trackHover: true,
							triggerElementRef: secondaryButtonRef,
							modifier: "cancel-email",
						});
					}
				}}
			/>
			<Button
				className={`${mainClass}__button ${mainClass}__button--primary`}
				onClick={() => {
					sendEmail();
				}}
				title={primary.label}
				disabled={loading}
				ref={sendButtonRef}
			/>
		</Fragment>
	);

	const isMainTitlePresent = !!pageHeader?.sections?.title;
	const displayHyperionAnimation =
		parseBoolean(emailData.displayOwl) && useHyperionTheme;

	return (
		<div className={mainClass} id={mainClass}>
			{displayHyperionAnimation && (
				<LottieAnimation
					id="emailAnimation"
					modifier="send-email"
					animation="_render_bird_email_animation_v1.json"
					isMainTitlePresent={isMainTitlePresent}
				/>
			)}
			{emailData.title && (
				<h2>
					<span
						className={`${mainClass}__title${
							!isMainTitlePresent && displayHyperionAnimation
								? ` ${mainClass}__title--main`
								: ""
						}`}
					>
						{emailData.title}
					</span>
					{emailData.subtitle && (
						<span className={`${mainClass}__sub-title`}>
							{emailData.subtitle}
						</span>
					)}
				</h2>
			)}
			{emailData.description && (
				<p
					className={`${mainClass}__description`}
					dangerouslySetInnerHTML={{ __html: emailData.description }}
				/>
			)}
			{emailData.displayOwl && !useHyperionTheme && (
				<Image
					image={{
						url: `${imagesLocation}/${owlImage}`,
						title: emailData?.imageAltText,
						baseClass: `${mainClass}__decorative-owl`,
					}}
				/>
			)}
			{showGenericErrorMessage && (
				<div className={`${mainClass}__generic-error`} role="alert">
					{config.cdnAssetsUrl && (
						<img
							alt={emailData.genericErrorMessageIconAlt || ""}
							aria-hidden="true"
							src={`${config.cdnAssetsUrl}/icons/pep/alert.svg`}
							loading="lazy"
						/>
					)}
					<p ref={genericErrorMessageRef}>{genericErrorMessage}</p>
				</div>
			)}
			{hasInputs && (
				<Fragment>
					{!useHyperionTheme && <Separator section="email" />}

					<div className={`${mainClass}__form`}>
						{emailData.optIn && (
							<h2 className={`${mainClass}__optin`}>
								{emailData.optIn}
							</h2>
						)}
						<span className={`${mainClass}__required`}>
							{emailData.requiredFields}
						</span>
						{name && (
							<InputText
								isHyperionTheme={useHyperionTheme}
								{...name}
								inlineErrors={inlineErrors}
								value={formInputs.name}
								handleChange={handleChange}
								validateInput={validateInput}
								isMobileView={isMobileView}
							/>
						)}
						<div className={`${mainClass}__group`}>
							{title && (
								<InputSelect
									{...title}
									inlineErrors={inlineErrors}
									modifier={title?.id}
									value={formInputs.title}
									handleChange={handleChange}
									validateInput={validateInput}
									showInlineErrors={false}
									isMobileView={isMobileView}
									useHyperionTheme={useHyperionTheme}
								/>
							)}
							{renderFirstNameLastNameFields()}
						</div>
						{inlineErrors && !isMobileView && !isCustomForm() && !isDCL && (
							<InlineErrors
								validate={[lastName?.id, firstName?.id, title?.id]}
								errorMessages={inlineErrors}
								modifier="group"
							/>
						)}
						{inlineErrors && isMobileView && !isCustomForm() && !isDCL && (
							<InlineErrors
								validate={[lastName?.id, title?.id]}
								errorMessages={inlineErrors}
								modifier="group"
							/>
						)}

						{telephone && (
							<InputText
								{...telephone}
								isHyperionTheme={useHyperionTheme}
								inlineErrors={inlineErrors}
								value={formInputs.telephone}
								handleChange={handleChange}
								validateInput={validateInput}
								isMobileView={isMobileView}
							/>
						)}
						{emailAddress && (
							<InputText
								{...emailAddress}
								isHyperionTheme={useHyperionTheme}
								inlineErrors={inlineErrors}
								value={formInputs.emailAddress}
								handleChange={handleChange}
								validateInput={validateInput}
								isMobileView={isMobileView}
								isEmailInput
							/>
						)}
						{reservationNumber && !fieldsToHide?.reservationNumber && (
							<InputText
								{...reservationNumber}
								isHyperionTheme={useHyperionTheme}
								inlineErrors={inlineErrors}
								value={formInputs.reservationNumber}
								handleChange={handleChange}
								validateInput={validateInput}
								isMobileView={isMobileView}
							/>
						)}
						{country && (
							<InputSelect
								{...country}
								inlineErrors={inlineErrors}
								value={formInputs.country}
								handleChange={handleChange}
								validateInput={validateInput}
								isMobileView={isMobileView}
								useHyperionTheme={useHyperionTheme}
							/>
						)}
						{!isMobileView && !useHyperionTheme && (
							<Separator section="email" />
						)}
						{category && (
							<InputSelect
								{...category}
								inlineErrors={inlineErrors}
								value={formInputs.category}
								handleChange={(e) => {
									scrollToRef(categoryRef);
									handleChange(e);
								}}
								validateInput={validateInput}
								isMobileView={isMobileView}
								useHyperionTheme={useHyperionTheme}
								formInputs={formInputs}
								ref={categoryRef}
							/>
						)}
						{subject && !fieldsToHide?.subject && (
							<InputSelect
								{...subject}
								inlineErrors={inlineErrors}
								value={formInputs.subject}
								validateInput={validateInput}
								isMobileView={isMobileView}
								useHyperionTheme={useHyperionTheme}
								handleChange={(e) => {
									scrollToRef(subjectRef);
									checkSubjectNote(e);
									return handleChange(e);
								}}
								ref={subjectRef}
							/>
						)}
						{subcategory && linkedFields.subcategory && (
							<InputSelect
								{...subcategory}
								options={linkedFields.subcategory?.options}
								inlineErrors={inlineErrors}
								value={formInputs.subcategory}
								validateInput={validateInput}
								isMobileView={isMobileView}
								handleChange={handleChange}
								formInputs={formInputs}
								useHyperionTheme={useHyperionTheme}
							/>
						)}
						{!!additionalFields.subject?.fields?.length && (
							<AdditionalFields
								additionalFields={additionalFields.subject}
								handleChange={handleChange}
								validateInput={validateInput}
								validateInputFile={validateInputFile}
								validateInputDate={validateInputDate}
								removeInputValue={removeInputValue}
								formInputs={formInputs}
								inlineErrors={inlineErrors}
								isMobileView={isMobileView}
								useHyperionTheme={useHyperionTheme}
							/>
						)}
						{useHyperionTheme &&
							!!additionalFields.subcategory?.fields?.length && (
								<Separator section="additional-fields" />
							)}
						{!!defaultFields?.fields?.length && (
							<AdditionalFields
								additionalFields={defaultFields}
								handleChange={handleChange}
								validateInput={validateInput}
								validateInputFile={validateInputFile}
								validateInputDate={validateInputDate}
								removeInputValue={removeInputValue}
								formInputs={formInputs}
								inlineErrors={inlineErrors}
								isMobileView={isMobileView}
								useHyperionTheme={useHyperionTheme}
							/>
						)}
						{!!additionalFields.subcategory?.fields?.length && (
							<Fragment>
								<AdditionalFields
									additionalFields={additionalFields.subcategory}
									conditionalsFields={conditionalsFields}
									handleChange={handleChange}
									validateInput={validateInput}
									validateInputFile={validateInputFile}
									handleMultipleFiles={handleMultipleFiles}
									validateInputDate={validateInputDate}
									removeInputValue={removeInputValue}
									formInputs={formInputs}
									inlineErrors={inlineErrors}
									isMobileView={isMobileView}
									useHyperionTheme={useHyperionTheme}
									imageLocation={imagesLocation}
									setFileRemoved={setFileRemoved}
									fileRemoved={fileRemoved}
									multipleAttractionsForms={multipleAttractionsForms}
									setMultipleAttractionsForms={setMultipleAttractionsForms}
									removeText={fileList?.removeText}
								/>
								{formInputs.files.length > 0 && useHyperionTheme && (
									<FileList
										{...fileList}
										files={formInputs.files}
										inlineErrors={inlineErrors}
										removeFile={removeFile}
										setFileRemoved={setFileRemoved}
									/>
								)}
							</Fragment>
						)}
						{formInputs?.subcategory === linkButton?.showOn &&
							multipleAttractionsfields.map((multipleAttractions, index) => {
								return (
									multipleAttractionsForms >= multipleAttractions?.index && (
										<Fragment>
											<Separator section="multipleAttractions" />
											<AdditionalFields
												key={index}
												multiple={isMultipleFile}
												additionalFields={multipleAttractions}
												conditionalsFields={
													conditionalsFieldsMultipleAttraction
												}
												handleChange={handleChange}
												validateInput={validateInput}
												validateInputFile={validateInputFile}
												handleMultipleFiles={handleMultipleFiles}
												validateInputDate={validateInputDate}
												removeInputValue={removeInputValue}
												formInputs={formInputs}
												inlineErrors={inlineErrors}
												isMobileView={isMobileView}
												useHyperionTheme={useHyperionTheme}
												imageLocation={imagesLocation}
												setFileRemoved={setFileRemoved}
												fileRemoved={fileRemoved}
												required={true}
												multipleAttractionsForms={multipleAttractionsForms}
												index={multipleAttractions?.index}
												setMultipleAttractionsForms={
													setMultipleAttractionsForms
												}
												parentClass={mainClass}
												removeText={fileList?.removeText}
												validateForm={validateForm}
											/>
										</Fragment>
									)
								);
							})}
						{emailMessage && !fieldsToHide?.emailMessage && (
							<InputTextArea
								{...emailMessage}
								isHyperionTheme={useHyperionTheme}
								inlineErrors={inlineErrors}
								value={formInputs.emailMessage}
								handleChange={handleChange}
								validateInput={validateInput}
								isMobileView={isMobileView}
								modifier={emailMessage?.id}
							/>
						)}
						{formInputs?.subcategory &&
							formInputs?.subcategory === linkButton?.showOn &&
							multipleAttractionsForms < 10 && (
								<div className={`${mainClass}__button-container`}>
									<Button
										className={`${mainClass}__button ${mainClass}__button--link`}
										title={linkButton?.label}
										customStyles
										childrenPosition={0}
										onClick={() => {
											setMultipleAttractionsForms(multipleAttractionsForms + 1);
										}}
									>
										<Icon icon="add-button"></Icon>
									</Button>
								</div>
							)}
						{useHyperionTheme ? (
							(!!formInputs.subcategory ||
								acceptPersonalData() ||
								formInputs.privacyPolicyStatement) && (
								<div className={`${mainClass}__button-container`}>
									{renderButtons}
								</div>
							)
						) : (
							<FixedToolbar>{renderButtons}</FixedToolbar>
						)}
					</div>
				</Fragment>
			)}
			{formNotes && (
				<div className={`${mainClass}__notes`}>
					<div
						className={`${mainClass}__notes__text`}
						dangerouslySetInnerHTML={{ __html: formNotes }}
					/>
				</div>
			)}
			{emailData.emailDisclaimer && (
				<div className={`${mainClass}__disclaimer`}>
					{emailData.emailDisclaimer.title && (
						<div
							className={`${mainClass}__disclaimer__title`}
							dangerouslySetInnerHTML={{
								__html: emailData.emailDisclaimer.title,
							}}
						/>
					)}

					{emailData.emailDisclaimer.body && (
						<div
							className={`${mainClass}__disclaimer__text`}
							dangerouslySetInnerHTML={{
								__html: emailData.emailDisclaimer.body,
							}}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default Email;
